//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type TourStep } from '@/components/Help/tourTypes';

/**
 * Whether a step's target is a selector that matches nothing a spotlight
 * could frame: no element, or one with no boxes, which is what display none
 * leaves of a parked panel and everything inside it. The 'none' placeholder
 * is not missing (the branch logic replaces or skips it), and an element
 * target is by definition present.
 */
export const isTargetMissing = (target: TourStep['target']): boolean => {
  if (typeof target !== 'string' || target === 'none') return false;
  const element = document.querySelector(target);
  return element === null || element.getClientRects().length === 0;
};

/**
 * The step the tour lands on when aimed at `index` while moving in
 * `direction`: `index` itself, or past it while the steps there said they may
 * be absent and are. May be past either end of the tour, which the caller
 * reads as the tour ending.
 *
 * `isMissing` is injectable because jsdom has no layout, so every element
 * there has no boxes.
 */
export const landingStep = (
  steps: readonly TourStep[], index: number, direction: 1 | -1,
  isMissing: (target: TourStep['target']) => boolean = isTargetMissing
): number => {
  let next = index;
  while (steps[next]?.skipIfAbsent && isMissing(steps[next].target))
    next += direction;
  return next;
};

/**
 * Whether a click on a click-to-advance step's target moves the tour on at
 * once. A step that declares the route it leads to is advanced by the route
 * change instead, unless the page is already there. One that declares none --
 * a click that opens a panel or a menu on the same page -- has nothing else
 * to wait for.
 */
export const clickAdvancesStep = (
  nextPath: string | undefined, pathname: string
): boolean => nextPath === undefined || pathname === nextPath;
